import React, { useState, useRef, useContext } from 'react';
import { FormGroup, Form, Input, Button, Row } from "reactstrap";
import CustomTable from '../components/CustomTable.jsx';
import PageHeader from '../../../components/Headers/PageHeader.jsx';
import BankAccountInfoModal from '../components/modals/BankAccountInfoModal.jsx';
import { AdminStateContext } from '../../../providers/AdminStateProvider.jsx';
import {
  getBankAccounts,
  getBankAccountRecord,
  searchBankAccountByProperty,
} from '../../../api';
import { useAuthToken } from '../../../hooks/AuthHook.jsx';

const SEARCH_BY_OWNER = 'owner';
const SEARCH_BY_PROPERTY = 'property';

const SEARCH_LABELS = {
  [SEARCH_BY_OWNER]: {
    hint: 'Ingresa el ID del propietario',
    placeholder: 'ID del propietario',
    action: 'Obtener cuentas bancarias',
    empty: 'Este propietario no tiene cuentas bancarias.',
  },
  [SEARCH_BY_PROPERTY]: {
    hint: 'Ingresa el ID de la propiedad',
    placeholder: 'ID de la propiedad',
    action: 'Buscar cuenta de la propiedad',
    empty: 'Esta propiedad no tiene una cuenta bancaria activa.',
  },
};

const describe = (error) => error?.message || 'No se pudo completar la búsqueda.';

const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [recordFetching, setRecordFetching] = useState(false);
  const [modalActive, setModalActive] = useState(false);
  const [bankAccountRecord, setBankAccountRecord] = useState([]);
  const [searchBy, setSearchBy] = useState(SEARCH_BY_OWNER);
  const [searchError, setSearchError] = useState('');
  // The owner of each row, keyed by account identifier. The record endpoint is
  // person-scoped, so opening the modal needs a person id; reading it off the
  // search input only worked while the input was always an owner id. In a
  // property search the input holds a property id, and the owner is whatever
  // the matched account says it is.
  const [ownerByAccount, setOwnerByAccount] = useState({});
  const [adminState,] = useContext(AdminStateContext);
  const inputEl = useRef();
  const authToken = useAuthToken();

  const labels = SEARCH_LABELS[searchBy];

  const columns = [
    'Identificador',
    'Titular',
    'Documento',
    'Banco',
    'Cuenta',
  ];
  const sizes = [2, 3, 2, 2, 3];

  // Every value in `ACCOUNT_TYPES` of ./bankAccountForm, which is the catalog
  // the create form submits: keep the two in step. MX's BANK and EDENRED were
  // missing here, so every Mexican account listed as "Otro" - invisible until
  // the console could create them.
  const parseBankAccountType = (type) => {
    switch (type) {
      case 'CURRENT':
        return 'Cuenta Corriente';
      case 'SIGHT':
        return 'Cuenta Vista';
      case 'SAVING':
        return 'Cuenta de Ahorro';
      case 'BANK':
        return 'Cuenta bancaria';
      case 'EDENRED':
        return 'Edenred';
      default:
        return 'Otro';
    }
  }

  // One row shape for both searches. The property search answers with
  // `property_id` where the person-scoped list answers with `identifier`: same
  // field, the API uses the domain name on the newer endpoint.
  const toRow = (account) => ({
    id: account.identifier ?? account.property_id,
    data: [
      account.identifier ?? account.property_id,
      account.holder,
      account.document,
      account.bank_name,
      `${parseBankAccountType(account.account_type)}: ${account.account_number}`,
    ],
  });

  const indexOwners = (accountList, fallbackOwnerId) =>
    accountList.reduce((owners, account) => {
      const rowId = account.identifier ?? account.property_id;
      return { ...owners, [rowId]: account.person_id ?? fallbackOwnerId };
    }, {});

  const showAccounts = (accountList, fallbackOwnerId) => {
    setAccounts(accountList.map(toRow));
    setOwnerByAccount(indexOwners(accountList, fallbackOwnerId));
  };

  const clearResults = () => {
    setAccounts([]);
    setOwnerByAccount({});
  };

  const fetchByOwner = async (ownerId) => {
    const response = await getBankAccounts(
      adminState.country.toLowerCase(),
      authToken,
      ownerId,
    );
    const accountList = response?.bank_accounts ?? [];
    showAccounts(accountList, ownerId);
    if (!accountList.length) setSearchError(labels.empty);
  };

  const fetchByProperty = async (propertyId) => {
    try {
      const account = await searchBankAccountByProperty(
        adminState.country.toLowerCase(),
        authToken,
        propertyId,
      );
      showAccounts([account]);
    } catch (error) {
      clearResults();
      // 404 is the documented answer for a property with no active account. It
      // is a result, not a failure, so it reads as the empty message rather
      // than as an error the operator should act on.
      setSearchError(error?.status === 404 ? labels.empty : describe(error));
    }
  };

  const search = async () => {
    const value = inputEl.current.value.trim();
    if (!value) return;

    setSearchError('');
    setIsFetching(true);
    try {
      if (searchBy === SEARCH_BY_PROPERTY) {
        await fetchByProperty(value);
      } else {
        await fetchByOwner(value);
      }
    } catch (error) {
      clearResults();
      setSearchError(describe(error));
    } finally {
      setIsFetching(false);
    }
  };

  const changeSearchBy = (mode) => {
    if (mode === searchBy) return;
    setSearchBy(mode);
    // The id in the input means something else now, and so do the rows below
    // it. Leaving either in place invites searching a property id against the
    // owner endpoint, or clicking a stale row.
    setSearchError('');
    clearResults();
    if (inputEl.current) inputEl.current.value = '';
  };

  const selectBankAccount = async (id) => {
    if (!id) return;
    const ownerId = ownerByAccount[id];
    if (!ownerId) {
      setSearchError('No se pudo determinar el propietario de esta cuenta.');
      return;
    }
    setRecordFetching(true);
    setModalActive(true);
    const response = await getBankAccountRecord(
      adminState.country.toLowerCase(),
      authToken,
      ownerId,
      id,
    );
    if (response && response.bank_accounts) {
      setBankAccountRecord(response.bank_accounts);
    }
    setRecordFetching(false);
  }

  return (
    <div>
      <PageHeader
        title={'Cuentas bancarias del propietario'}
        breadcrumb={[{ href: '#', title: 'Propietarios' }]}
      />
      <BankAccountInfoModal
        active={modalActive}
        onClose={() => setModalActive(false)}
        bankAccountRecord={bankAccountRecord}
        loading={recordFetching}
      />
      <CustomTable
        columns={columns}
        sizes={sizes}
        data={accounts}
        loading={isFetching}
        onClick={selectBankAccount}
        renderHeader={() => (
          <Form onSubmit={e => e.preventDefault()}>
            <FormGroup className="m-auto">
              <Row className="mx-2 mb-2">
                <Button
                  color={searchBy === SEARCH_BY_OWNER ? 'primary' : 'secondary'}
                  size="sm"
                  type="button"
                  onClick={() => changeSearchBy(SEARCH_BY_OWNER)}
                >
                  Por propietario
                </Button>
                <Button
                  color={searchBy === SEARCH_BY_PROPERTY ? 'primary' : 'secondary'}
                  size="sm"
                  type="button"
                  onClick={() => changeSearchBy(SEARCH_BY_PROPERTY)}
                >
                  Por propiedad
                </Button>
              </Row>
              <label><small>{labels.hint}</small></label>
              <Row>
                <Input
                  className="col form-control-muted form-control-sm mx-2"
                  placeholder={labels.placeholder}
                  type="text"
                  innerRef={inputEl}
                />
                <Button
                  color="primary"
                  size="sm"
                  type="button"
                  onClick={search}
                >
                  {labels.action}
                </Button>
              </Row>
              {searchError && (
                <small className="text-muted mx-2">{searchError}</small>
              )}
            </FormGroup>
          </Form>
        )}
      />
    </div>
  );
}

export default Accounts;
